import React, { useState } from 'react';
import { Download } from 'lucide-react';

interface SpecMapDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  searchTerm?: string;
  matchesWildcard?: (text: string, pattern: string) => boolean;
  showToggle?: boolean;
  onAddToInput?: (text: string) => void;
  onCopyResult?: () => void;
  showToast?: (message: string) => void;
  showHeader?: boolean;
}

const SpecMapDisplay: React.FC<SpecMapDisplayProps> = ({
  result,
  theme = 'light',
  searchTerm = '',
  matchesWildcard,
  showToggle = false,
  onAddToInput,
  onCopyResult,
  showToast,
  showHeader = true
}) => {
  const [showStructured, setShowStructured] = useState(false)
  const [expandedNodes, setExpandedNodes] = useState(new Set())

  // Check if the result looks like a spec map object
  const isSpecMapResult = (data) => {
    if (!data || typeof data !== 'object') return false
    
    // First check if there's a spec_map property containing the actual spec map
    if (data.spec_map && typeof data.spec_map === 'object') {
      return isSpecMapResult(data.spec_map) // Recursively check the spec_map contents
    }
    
    // Check for direct spec_map structure (result contains component objects directly)
    const keys = Object.keys(data)
    if (keys.length === 0) return false
    
    // Check if entries have spec map component structure
    let validEntries = 0
    keys.forEach(key => {
      const entry = data[key]
      if (entry && typeof entry === 'object' && 
          (entry.reference_numeral !== undefined || 
           entry.children !== undefined || 
           entry.characteristics !== undefined ||
           entry.description !== undefined ||
           entry.definition !== undefined ||
           entry.citations !== undefined ||
           entry.pseudonyms !== undefined)) {
        validEntries++
      }
    })
    
    // If at least 50% of entries match the pattern, consider it a spec map
    return validEntries / keys.length >= 0.5
  }

  // Recursive function to render children at any depth
  const renderChildren = (children, level) => {
    let childrenContent = `<div class="children level-${level}"><strong>Sub-components:</strong>`;

    Object.entries(children).forEach(([childKey, childComponent]: [string, any]) => {
      const indentClass = level > 1 ? `style="margin-left: ${(level - 1) * 20}px;"` : '';
      childrenContent += `
        <div class="child-component" ${indentClass}>
          <div style="font-weight: bold;">${childKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
      `;
      
      if (childComponent.reference_numeral !== undefined) {
        childrenContent += `<div class="reference-numeral"><strong>Reference Numeral:</strong> ${childComponent.reference_numeral}</div>`;
      }
      
      if (childComponent.description) {
        childrenContent += `<div><strong>Description:</strong> ${childComponent.description}</div>`;
      }
      
      if (childComponent.definition) {
        childrenContent += `<div class="definition"><strong>Definition:</strong> ${childComponent.definition}</div>`;
      }
      
      if (childComponent.citations && childComponent.citations.length > 0) {
        childrenContent += `<div class="citations"><strong>Citations:</strong> ${childComponent.citations.join(', ')}</div>`;
      }
      
      // Recursively render nested children
      if (childComponent.children && Object.keys(childComponent.children).length > 0) {
        childrenContent += renderChildren(childComponent.children, level + 1);
      }
      
      childrenContent += '</div>';
    });
    
    childrenContent += '</div>';
    return childrenContent;
  };

  const exportToDocx = () => {
    try {
      let docContent = `
        <html>
          <head>
            <title>Specification Map</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              h1 { color: #333; border-bottom: 2px solid #333; }
              h2 { color: #666; margin-top: 25px; }
              .component { margin: 20px 0; padding: 15px; border: 1px solid #ddd; border-radius: 5px; }
              .component-title { font-weight: bold; font-size: 16px; color: #333; margin-bottom: 10px; }
              .reference-numeral { color: #2563eb; font-weight: bold; }
              .description { margin: 8px 0; }
              .definition { margin: 8px 0; background-color: #fff3cd; padding: 8px; border-radius: 4px; }
              .citations { margin: 8px 0; font-style: italic; color: #28a745; }
              .children { margin: 15px 0 0 20px; padding-left: 20px; border-left: 2px solid #ddd; }
              .child-component { margin: 10px 0; padding: 10px; background-color: #f8f9fa; border-radius: 4px; }
              .level-2 { margin-left: 20px; }
              .level-3 { margin-left: 40px; }
              .level-4 { margin-left: 60px; }
            </style>
          </head>
          <body>
            <h1>Specification Map</h1>
      `;

      // Convert spec map to formatted content
      Object.entries(specMap).forEach(([key, component]: [string, any]) => {
        docContent += `
          <div class="component">
            <div class="component-title">${key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
        `;

        if (component.reference_numeral !== undefined) {
          docContent += `<div class="reference-numeral"><strong>Reference Numeral:</strong> ${component.reference_numeral}</div>`;
        }

        if (component.description) {
          docContent += `<div class="description"><strong>Description:</strong> ${component.description}</div>`;
        }

        if (component.definition) {
          docContent += `<div class="definition"><strong>Definition:</strong> ${component.definition}</div>`;
        }

        if (component.citations && component.citations.length > 0) {
          docContent += `<div class="citations"><strong>Citations:</strong> ${component.citations.join(', ')}</div>`;
        }

        // Handle children recursively
        if (component.children && Object.keys(component.children).length > 0) {
          docContent += renderChildren(component.children, 1);
        }

        docContent += '</div>';
      });

      docContent += '</body></html>';

      // Create and download the file
      const blob = new Blob([docContent], { type: 'application/msword' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'specification-map.doc';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (showToast) {
        showToast('Specification map exported to Word document');
      }
    } catch (error) {
      console.error('Error exporting spec map to DOCX:', error);
      if (showToast) {
        showToast('Error exporting specification map');
      }
    }
  }

  if (!isSpecMapResult(result)) {
    return null // Not a spec map, don't render this component
  }

  // Extract the actual spec map data, handling both wrapped and direct formats
  const specMap = result.spec_map && typeof result.spec_map === 'object' ? result.spec_map : result

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes)
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId)
    } else {
      newExpanded.add(nodeId)
    }
    setExpandedNodes(newExpanded)
  }

  const formatComponentName = (name) => {
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const formatCitationRanges = (citations) => {
    if (!citations || citations.length === 0) return []
    
    // Extract numeric values from citations like [0020], [0021], etc.
    const parsedCitations = citations.map(citation => {
      const match = citation.match(/\[(\d+)\]/)
      return {
        original: citation,
        number: match ? parseInt(match[1], 10) : null,
        hasNumber: !!match
      }
    })
    
    // Separate numbered citations from non-numbered ones
    const numberedCitations = parsedCitations.filter(c => c.hasNumber).sort((a, b) => a.number - b.number)
    const nonNumberedCitations = parsedCitations.filter(c => !c.hasNumber)
    
    // Group consecutive numbers into ranges
    const ranges = []
    let currentRange = []
    
    for (let i = 0; i < numberedCitations.length; i++) {
      const current = numberedCitations[i]
      
      if (currentRange.length === 0) {
        currentRange = [current]
      } else {
        const last = currentRange[currentRange.length - 1]
        if (current.number === last.number + 1) {
          currentRange.push(current)
        } else {
          // End current range and start new one
          if (currentRange.length >= 3) {
            // Show as range if 3 or more consecutive
            ranges.push(`[${String(currentRange[0].number).padStart(4, '0')}]-[${String(currentRange[currentRange.length - 1].number).padStart(4, '0')}]`)
          } else {
            // Show individually if less than 3 consecutive
            ranges.push(...currentRange.map(c => c.original))
          }
          currentRange = [current]
        }
      }
    }
    
    // Handle the last range
    if (currentRange.length > 0) {
      if (currentRange.length >= 3) {
        ranges.push(`[${String(currentRange[0].number).padStart(4, '0')}]-[${String(currentRange[currentRange.length - 1].number).padStart(4, '0')}]`)
      } else {
        ranges.push(...currentRange.map(c => c.original))
      }
    }
    
    // Add non-numbered citations at the end
    ranges.push(...nonNumberedCitations.map(c => c.original))
    
    return ranges
  }

  const getAllNodeIds = (specMapData, level = 0) => {
    const nodeIds = []

    Object.entries(specMapData).forEach(([key, component]: [string, any]) => {
      const nodeId = `${level}-${key}`
      nodeIds.push(nodeId)
      
      // Recursively get node IDs for children
      if (component.children && Object.keys(component.children).length > 0) {
        nodeIds.push(...getAllNodeIds(component.children, level + 1))
      }
    })
    
    return nodeIds
  }

  const matchesSearch = (component) => {
    if (!searchTerm || !matchesWildcard) return true
    
    const name = formatComponentName(component.key)
    const description = component.description || ''
    const definition = component.definition || ''
    const pseudonyms = component.pseudonyms ? component.pseudonyms.join(' ') : ''
    const citations = component.citations ? component.citations.join(' ') : ''
    
    let characteristics = ''
    if (component.characteristics) {
      if (Array.isArray(component.characteristics)) {
        characteristics = component.characteristics.map(char => 
          typeof char === 'object' ? char.characteristic || JSON.stringify(char) : char
        ).join(' ')
      } else if (typeof component.characteristics === 'object') {
        characteristics = Object.entries(component.characteristics).map(([key, value]: [string, any]) => 
          typeof value === 'object' ? value.characteristic || `${key} ${JSON.stringify(value)}` : `${key} ${value}`
        ).join(' ')
      } else {
        characteristics = component.characteristics
      }
    }
    
    return matchesWildcard(name, searchTerm) ||
           matchesWildcard(description, searchTerm) ||
           matchesWildcard(definition, searchTerm) ||
           matchesWildcard(pseudonyms, searchTerm) ||
           matchesWildcard(citations, searchTerm) ||
           matchesWildcard(characteristics, searchTerm)
  }

  const renderComponent = (key, component, level = 0) => {
    const nodeId = `${level}-${key}`
    const isExpanded = expandedNodes.has(nodeId)
    const hasChildren = component.children && Object.keys(component.children).length > 0
    const indentClass = level > 0 ? `ml-${Math.min(level * 4, 16)}` : ''

    // Filter based on search
    if (!matchesSearch({ key, ...component })) {
      return null
    }

    return (
      <div key={nodeId} className={`${indentClass} border-l-2 ${level > 0 ? (theme === 'dark' ? 'border-gray-600' : 'border-gray-300') : 'border-transparent'} ${level > 0 ? 'pl-4 ml-2' : ''}`}>
        <div className={`p-3 rounded-lg mb-2 transition-all duration-200 ${
          theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'
        }`}>
          <div className="flex items-start gap-3">
            {/* Expand/Collapse Button */}
            
            <div className="flex-1">
              {/* Component Name and Reference Numeral */}
              <div className={`font-semibold text-lg mb-2 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                {formatComponentName(key)}
                {component.reference_numeral && (
                  <span className={`ml-3 font-bold text-xl ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                    {component.reference_numeral}
                  </span>
                )}
                {hasChildren && (
                  <button
                    onClick={() => toggleNode(nodeId)}
                    className={`mt-1 p-1 rounded transition-colors ${
                      theme === 'dark' ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
                    }`}
                  >
                    <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Description */}
              {component.description && (
                <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {component.description}
                </div>
              )}

              {/* Definition */}
              {component.definition && (
                <div className={`text-sm mb-2 p-2 rounded-md ${theme === 'dark' ? 'bg-gray-600' : 'bg-blue-50'}`}>
                  <span className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-blue-600'}`}>
                    Definition: 
                  </span>
                  <span className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    {component.definition}
                  </span>
                </div>
              )}

              {/* Pseudonyms */}
              {component.pseudonyms && component.pseudonyms.length > 0 && (
                <div className="mb-2">
                  <span className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Also known as:{' '}
                  </span>
                  <span className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    {component.pseudonyms.join(', ')}
                  </span>
                </div>
              )}

              {/* Characteristics */}
              {component.characteristics && (
                <div className="mb-2">
                  <div className={`text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Characteristics:
                  </div>
                  <div className="space-y-1">
                    {Array.isArray(component.characteristics) ? (
                      component.characteristics.map((char, idx) => (
                        <div key={idx} className={`p-2 rounded-md ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-100'}`}>
                          {typeof char === 'object' && char.characteristic ? (
                            <div>
                              <div className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                                {char.characteristic}
                              </div>
                              {char.citation && (
                                <div className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                  📖 Citation: {char.citation}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                              {typeof char === 'object' ? JSON.stringify(char) : char}
                            </div>
                          )}
                        </div>
                      ))
                    ) : typeof component.characteristics === 'object' ? (
                      Object.entries(component.characteristics).map(([key, value]: [string, any], idx) => (
                        <div key={idx} className={`p-2 rounded-md ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-100'}`}>
                          <div className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                            {key}
                          </div>
                          {typeof value === 'object' && value.characteristic ? (
                            <div>
                              <div className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                {value.characteristic}
                              </div>
                              {value.citation && (
                                <div className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                  📖 Citation: {value.citation}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                              {typeof value === 'object' ? JSON.stringify(value) : value}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className={`p-2 rounded-md ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-100'}`}>
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          {component.characteristics}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Citations */}
              {component.citations && component.citations.length > 0 && (
                <div className="mb-2">
                  <div className={`text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Citations:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {formatCitationRanges(component.citations).map((citation, idx) => (
                      <span key={idx} className={`inline-block px-2 py-1 text-xs rounded-full ${
                        theme === 'dark' ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'
                      }`}>
                        📖 {citation}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="ml-4">
            {Object.entries(component.children).map(([childKey, childComponent]: [string, any]) =>
              renderComponent(childKey, childComponent, level + 1)
            )}
          </div>
        )}
      </div>
    )
  }

  const filteredComponents = Object.entries(specMap).filter(([key, component]: [string, any]) =>
    matchesSearch({ key, ...component })
  )

  const renderFormattedSpecMap = () => {
    if (searchTerm && filteredComponents.length === 0) {
      return (
        <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
          <div className="text-lg mb-2">No components found</div>
          <div className="text-sm">Try adjusting your search term or using wildcards (* for any characters)</div>
        </div>
      )
    }

    return (
      <div className="space-y-2">
        {Object.entries(specMap).map(([key, component]: [string, any]) =>
          renderComponent(key, component, 0)
        )}
      </div>
    )
  }

  const renderStructuredData = () => {
    return (
      <pre className={`text-sm whitespace-pre-wrap font-mono p-4 rounded-md  overflow-y-auto ${
        theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'
      }`}>
        {JSON.stringify(result, null, 2)}
      </pre>
    )
  }

  return (
    <div className={showHeader ? "space-y-3" : ""}>
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            🗺️ Specification Map
          </div>
          {showToggle && (
            <div className="flex items-center gap-2">
              {onAddToInput && (
                <button
                  onClick={() => {
                    onAddToInput(typeof result === 'object' ? JSON.stringify(result, null, 2) : result);
                    if (showToast) showToast('Result added to chat input!');
                  }}
                  className={`text-xs py-1 px-3 rounded-md transition-colors ${
                    theme === 'dark' ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  Add to Chat
                </button>
              )}
              <button
                onClick={exportToDocx}
                className={`text-xs py-1 px-3 rounded-md transition-colors flex items-center gap-1 ${
                  theme === 'dark' ? 'bg-green-600 text-white hover:bg-green-500' : 'bg-green-500 text-white hover:bg-green-600'
                }`}
                title="Export specification map as Word document"
              >
                <Download className="w-3 h-3" />
                Export DOCX
              </button>
              <button
                onClick={() => {
                  if (onCopyResult) onCopyResult();
                }}
                className={`text-xs py-1 px-3 rounded-md transition-colors ${
                  theme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Copy
              </button>
              <button
                onClick={() => setShowStructured(!showStructured)}
                className={`text-xs py-1 px-3 rounded-md transition-colors ${
                  showStructured
                    ? theme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                    : theme === 'dark' ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {showStructured ? 'Show Formatted' : 'Show Raw Data'}
              </button>
              <button
                onClick={() => setExpandedNodes(new Set())}
                className={`text-xs py-1 px-3 rounded-md transition-colors ${
                  theme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Collapse All
              </button>
              <button
                onClick={() => setExpandedNodes(new Set(getAllNodeIds(specMap)))}
                className={`text-xs py-1 px-3 rounded-md transition-colors ${
                  theme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Expand All
              </button>
            </div>
          )}
        </div>
      )}

      <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
        {showStructured ? renderStructuredData() : renderFormattedSpecMap()}
      </div>
    </div>
  )
}

export default SpecMapDisplay